<template>

    <v-card class="cost-analysis card-standard pa-4">
      <v-card-title tag="h2" class="typography-h5 weight-bold">
        {{ $t('analysis.costEfficiency.title') }}
      </v-card-title>

      <template v-if="loading">
        <!-- Skeleton mirrors the final tab layout so perceived
             loading time drops vs. a centered spinner with no
             structure cue. -->
        <v-card-text class="overall-score-hero mb-2 skeleton-hero" data-testid="cost-skeleton">
          <v-skeleton-loader type="heading" />
          <v-skeleton-loader type="image" height="10" class="mt-2" />
          <div class="d-flex mt-3 flex-wrap" style="gap: 6px;">
            <v-skeleton-loader type="chip" />
            <v-skeleton-loader type="chip" />
          </div>
        </v-card-text>
        <v-card-text
          v-for="i in 3"
          :key="`sk-${i}`"
          class="analysis-section"
        >
          <v-skeleton-loader type="heading, sentences, sentences" />
        </v-card-text>
        <div class="d-flex align-center justify-center pa-3 text-caption text-medium-emphasis" aria-live="polite">
          <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
          <span>{{ $t('analysis.costEfficiency.loadingDetail') }}</span>
        </div>
      </template>

      <template v-else-if="hasError">
        <div class="text-center error--text">
          {{ $t('analysis.costEfficiency.error.default') }}
          <br />
          <v-btn
            color="primary"
            variant="text"
            class="mt-2"
            :loading="loading"
            @click="fetchCostAnalysis"
          >
            <v-icon icon="mdi-refresh" class="mr-2" />
            {{ $t('common.retry') }}
          </v-btn>
        </div>
      </template>
      
      <template v-else-if="analysis">
        <!-- Overall score hero — premium-gated except for the score itself,
             which doubles as a teaser. -->
        <v-card-text v-if="overallScore > 0" class="overall-score-hero mb-2">
          <div class="d-flex justify-space-between align-center mb-2 flex-wrap" style="gap: 8px;">
            <h3 class="typography-h6 weight-semibold">
              {{ $t('analysis.costEfficiency.sections.overallScore.title') }}
            </h3>
            <span class="overall-score-value" :class="`overall-score--${scoreLevel(overallScore)}`">
              {{ overallScore }}<span class="overall-score-max">/5</span>
            </span>
          </div>
          <v-progress-linear
            :model-value="overallScore * 20"
            :color="scoreColor(overallScore)"
            height="10"
            rounded
            class="mb-1"
          />
          <div class="score-chip-row d-flex flex-wrap mt-2">
            <v-chip
              v-if="costRangeLevel"
              :color="costRangeColor(costRangeLevel)"
              size="small"
              variant="tonal"
              class="score-chip"
              prepend-icon="mdi-currency-eur"
            >
              {{ $t(`analysis.costEfficiency.costLevel.${costRangeLevel}`) }}
            </v-chip>
            <v-chip
              v-if="energyClassEstimate"
              :color="energyClassColor(energyClassEstimate)"
              size="small"
              variant="tonal"
              class="score-chip"
              prepend-icon="mdi-leaf"
            >
              {{ $t('analysis.costEfficiency.sections.energyClass.title') }}: {{ energyClassLabel(energyClassEstimate) }}
            </v-chip>
          </div>
        </v-card-text>

        <!-- Cost Drivers and Quick Wins are pinned to the top so users see
             the actionable, conversion-relevant content without scrolling
             through the long aspect descriptions first. -->

        <!-- Top Cost Drivers -->
        <v-card-text v-if="costDrivers.length > 0" class="analysis-section" :class="{ 'premium-item': !hasPremiumAccess }">
          <h3 class="typography-h6 weight-semibold mb-3">
            {{ $t('analysis.costEfficiency.sections.costDrivers.title') }}
          </h3>
          <PremiumContent
            :is-premium="!hasPremiumAccess"
            preview-content=""
            cta-key="analysis.costEfficiency.premiumCta.costDrivers"
            @upgrade="handlePremiumContent('cost_drivers')"
          >
            <ul class="driver-list pa-0 ma-0">
              <li
                v-for="(driver, idx) in costDrivers"
                :key="idx"
                class="driver-item"
              >
                <div class="d-flex align-start" style="gap: 8px;">
                  <v-chip
                    :color="severityColor(driver.severity)"
                    size="x-small"
                    variant="tonal"
                    class="driver-severity"
                  >
                    {{ $t(`analysis.costEfficiency.severity.${driver.severity}`) }}
                  </v-chip>
                  <div class="flex-grow-1">
                    <div class="weight-semibold">{{ driver.title }}</div>
                    <div class="typography-body text-secondary">{{ driver.description }}</div>
                    <button
                      v-if="hasPremiumAccess"
                      type="button"
                      class="quick-read-studio-link mt-2"
                      :data-testid="`studio-link-cost-driver-${idx}`"
                      @click="openCostDriverInStudio(driver)"
                    >
                      <v-icon icon="mdi-auto-fix" size="14" />
                      {{ $t('analysis.results.quickRead.openInStudio') }}
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </PremiumContent>
        </v-card-text>

        <!-- Quick Wins -->
        <v-card-text v-if="quickWins.length > 0" class="analysis-section analysis-section--quickwins" :class="{ 'premium-item': !hasPremiumAccess }">
          <h3 class="typography-h6 weight-semibold mb-3">
            <v-icon icon="mdi-lightbulb-on-outline" class="mr-1" size="small" />
            {{ $t('analysis.costEfficiency.sections.quickWins.title') }}
          </h3>
          <PremiumContent
            :is-premium="!hasPremiumAccess"
            preview-content=""
            cta-key="analysis.costEfficiency.premiumCta.quickWins"
            @upgrade="handlePremiumContent('quick_wins')"
          >
            <ul class="quickwin-list pa-0 ma-0">
              <li
                v-for="(win, idx) in quickWins"
                :key="idx"
                class="quickwin-item"
              >
                <div class="d-flex align-start" style="gap: 8px;">
                  <v-chip
                    :color="impactColor(win.estimatedImpact)"
                    size="x-small"
                    variant="tonal"
                    class="quickwin-impact"
                  >
                    {{ $t(`analysis.costEfficiency.impact.${win.estimatedImpact}`) }}
                  </v-chip>
                  <div class="flex-grow-1">
                    <div class="weight-semibold">{{ win.title }}</div>
                    <div class="typography-body text-secondary">{{ win.rationale }}</div>
                  </div>
                </div>
              </li>
            </ul>
          </PremiumContent>
        </v-card-text>

        <!-- Climate Resilience — first of the deeper aspect sections; the
             only one a trial user can read in full. -->
        <v-card-text class="analysis-section analysis-section--featured">
          <div class="d-flex justify-space-between align-center flex-wrap mb-2" style="gap: 6px;">
            <h3 class="typography-h6 weight-semibold">
              {{ $t('analysis.costEfficiency.sections.climateResilience.title') }}
            </h3>
            <v-rating
              :model-value="analysis.climateResilience.score"
              readonly
              density="compact"
              color="primary"
              length="5"
            ></v-rating>
          </div>
          <ConfidenceChip :confidence="analysis.climateResilience.confidence" class="mb-2" />
          <span class="typography-body text-secondary analysis-description text-wrap"
                v-html="formatMarkdownLite(analysis.climateResilience.description)">
          </span>
        </v-card-text>

        <!-- Construction Cost -->
        <v-card-text class="analysis-section" :class="{ 'premium-item': !hasPremiumAccess }">
          <div class="d-flex justify-space-between align-center flex-wrap mb-2" style="gap: 6px;">
            <h3 class="typography-h6 weight-semibold">
              {{ $t('analysis.costEfficiency.sections.constructionCost.title') }}
            </h3>
            <v-rating
              :model-value="analysis.constructionCost.score"
              readonly
              density="compact"
              color="primary"
              length="5"
            ></v-rating>
          </div>
          <ConfidenceChip :confidence="analysis.constructionCost.confidence" class="mb-2" />
          <PremiumContent
            :is-premium="!hasPremiumAccess"
            :preview-content="analysis.constructionCost.description.substring(0, 0)"
            cta-key="analysis.costEfficiency.premiumCta.constructionCost"
            @upgrade="handlePremiumContent('construction_cost')"
          >
            <span class="typography-body text-secondary analysis-description text-wrap"
                  v-html="formatMarkdownLite(analysis.constructionCost.description)">
            </span>
          </PremiumContent>
        </v-card-text>

        <!-- Energy Efficiency -->
        <v-card-text class="analysis-section" :class="{ 'premium-item': !hasPremiumAccess }">
          <div class="d-flex justify-space-between align-center flex-wrap mb-2" style="gap: 6px;">
            <h3 class="typography-h6 weight-semibold">
              {{ $t('analysis.costEfficiency.sections.energyEfficiency.title') }}
            </h3>
            <v-rating
              :model-value="analysis.energyEfficiency.score"
              readonly
              density="compact"
              color="primary"
              length="5"
            ></v-rating>
          </div>
          <ConfidenceChip :confidence="analysis.energyEfficiency.confidence" class="mb-2" />
          <PremiumContent
            :is-premium="!hasPremiumAccess"
            :preview-content="analysis.energyEfficiency.description.substring(0, 0)"
            cta-key="analysis.costEfficiency.premiumCta.energyEfficiency"
            @upgrade="handlePremiumContent('energy_efficiency')"
          >
            <span class="typography-body text-secondary analysis-description text-wrap"
                  v-html="formatMarkdownLite(analysis.energyEfficiency.description)">
            </span>
          </PremiumContent>
        </v-card-text>

        <!-- Summary — closes the tab as a true 'Fazit'. Cost Drivers and
             Quick Wins now live at the top of the card, above the
             aspect descriptions. -->
        <v-card-text v-if="analysis.summary" class="analysis-summary mt-4" :class="{ 'premium-item': !hasPremiumAccess }">
          <div class="typography-h6 weight-semibold mb-2">
            {{ $t('analysis.costEfficiency.sections.summary.title') }}
          </div>
          <PremiumContent
            :is-premium="!hasPremiumAccess"
            :preview-content="analysis.summary.substring(0, 0)"
            cta-key="analysis.costEfficiency.premiumCta.summary"
            @upgrade="handlePremiumContent('summary')"
          >
            <span class="typography-body text-secondary analysis-description text-wrap"
                  v-html="formatMarkdownLite(analysis.summary)">
            </span>
          </PremiumContent>
        </v-card-text>

        <!-- Provenance + disclaimer footer -->
        <v-card-text class="cost-footer text-caption text-medium-emphasis">
          <div v-if="generatedAtLabel" data-testid="cost-meta-footer">
            {{ $t('analysis.costEfficiency.generatedAt', { date: generatedAtLabel }) }}<template v-if="modelTierLabel"> · {{ modelTierLabel }}</template>
          </div>
          <div class="mt-1" data-testid="cost-disclaimer">
            {{ $t('analysis.costEfficiency.disclaimer') }}
          </div>
        </v-card-text>
      </template>
    </v-card>

</template>

<script setup>
import { onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import PremiumContent from '@/components/common/PremiumContent.vue'
import ConfidenceChip from '@/components/analysis/ConfidenceChip.vue'
import { trackCostEfficiencyPremiumClick, trackUnlockCtaClicked } from '@/utils/analytics'
import { formatMarkdownLite } from '@/utils/textFormatting'
import { getIdToken } from 'firebase/auth'
import { useStore } from 'vuex'
import { scrollToUnlockPanel } from '@/utils/scrollToUnlockPanel'
import { buildUnlockCtaPayload } from '@/utils/unlockTracking.mjs'

const { locale, t, te } = useI18n()
const authStore = useAuthStore()
const store = useStore()
const route = useRoute()
const router = useRouter()

// Per v2 spec §14: cost-driver rows offer a one-click jump into Studio
// with a templated directive.
const analysisRouteId = computed(() => String(route.params.id || store.state.analysis?.analysisId || 'current'))
const openCostDriverInStudio = (driver) => {
  if (!driver) return
  const label = driver.title || driver.description || 'Kostentreiber'
  router.push({
    name: 'AnalysisStudio',
    params: { id: analysisRouteId.value },
    query: {
      prefill: `Baukosten reduzieren für: ${label}.`,
      source: 'cost_driver',
    },
  })
}

// Compute whether user has access to premium content
const hasPremiumAccess = computed(() => store.state.userHadPremiumWhenUploaded)

const props = defineProps({
  imageUrl: {
    type: String,
    required: true
  }
})

const sectionStatus = computed(() => store.state.analysisSectionStatus?.costs || 'idle')
const loading = computed(() => sectionStatus.value === 'loading')
const hasError = computed(() => sectionStatus.value === 'error')
const analysis = computed(() => store.state.analysisCost || {
  climateResilience: { score: 0, description: '' },
  constructionCost: { score: 0, description: '' },
  energyEfficiency: { score: 0, description: '' },
  summary: ''
})

// Schema v1.1 fields — guard with optional chaining so the component
// renders cleanly against legacy stored analyses that lack them.
const costDrivers = computed(() => Array.isArray(analysis.value.costDrivers) ? analysis.value.costDrivers : [])
const quickWins = computed(() => Array.isArray(analysis.value.quickWins) ? analysis.value.quickWins : [])
const costRangeLevel = computed(() => analysis.value.costRangeQualitative?.level || null)
const energyClassEstimate = computed(() => analysis.value.energyEfficiencyClass?.estimate || null)
const overallScore = computed(() => {
  const v = analysis.value.overallScore
  return Number.isFinite(v) ? v : 0
})

// _meta footer — generatedAt + modelTier. Legacy stored analyses
// without _meta render the disclaimer alone.
const meta = computed(() => analysis.value._meta || null)
const generatedAtLabel = computed(() => {
  const iso = meta.value?.generatedAt
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString(locale.value, { dateStyle: 'long', timeStyle: 'short' })
})
const modelTierLabel = computed(() => {
  const tier = meta.value?.modelTier
  if (!tier) return null
  const key = `analysis.costEfficiency.modelTierLabel.${tier}`
  return te(key) ? t(key) : null
})

const scoreColor = (score) => {
  if (score >= 4) return 'success'
  if (score >= 3) return 'warning'
  return 'error'
}
const scoreLevel = (score) => {
  if (score >= 4) return 'good'
  if (score >= 3) return 'mid'
  return 'low'
}
const severityColor = (severity) => {
  if (severity === 'high') return 'error'
  if (severity === 'medium') return 'warning'
  return 'grey'
}
const impactColor = (impact) => {
  if (impact === 'high') return 'success'
  if (impact === 'medium') return 'primary'
  return 'grey'
}
const costRangeColor = (level) => {
  if (level === 'low') return 'success'
  if (level === 'medium') return 'primary'
  return 'warning'
}
const energyClassColor = (estimate) => {
  if (['A+', 'A'].includes(estimate)) return 'success'
  if (['B', 'C'].includes(estimate)) return 'primary'
  if (estimate === 'D') return 'warning'
  return 'grey'
}
const energyClassLabel = (estimate) => {
  if (estimate === 'unbestimmt') {
    return t('analysis.costEfficiency.energyClass.unbestimmt')
  }
  return estimate
}

const fetchCostAnalysis = async () => {
  if (loading.value) {
    return;
  }

  // If we already have analysis data in store and the imageUrl hasn't changed, use that
  if (store.state.analysisCost && store.state.lastAnalyzedImage === props.imageUrl) {
    return;
  }
  
  try {
    const authHeader = authStore.isAuthenticated
      ? `Bearer ${await getIdToken(authStore.user)}`
      : undefined

    await store.dispatch('preloadCostAnalysis', {
      imageUrl: props.imageUrl,
      language: locale.value,
      authHeader,
    })
  } catch (err) {
    console.error('CostEfficiencyAnalysis: Analysis failed:', err)
  }
}

// Update onMounted to check for preloaded data
onMounted(() => {
  if (props.imageUrl) {
    if (!store.state.analysisCost && sectionStatus.value === 'idle') {
      fetchCostAnalysis()
    }
  }
})

// Add navigation helper
const navigateToPricing = () => {
  scrollToUnlockPanel()
}

// Add premium content handler
const handlePremiumContent = (section) => {
  trackCostEfficiencyPremiumClick(section)
  trackUnlockCtaClicked(buildUnlockCtaPayload({
    source: 'cost_efficiency',
    userIntent: store.state.userIntent,
    extra: {
      section,
    },
  }))
  
  if (!hasPremiumAccess.value) {
    navigateToPricing()
  }
}
</script>

<style scoped>
.cost-analysis {
  border-radius: 8px;
}

.overall-score-hero {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.06), rgba(var(--v-theme-primary), 0.02));
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.12);
}

.overall-score-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.overall-score-value.overall-score--good { color: rgb(var(--v-theme-success)); }
.overall-score-value.overall-score--mid { color: rgb(var(--v-theme-warning)); }
.overall-score-value.overall-score--low { color: rgb(var(--v-theme-error)); }

.overall-score-max {
  font-size: 1rem;
  font-weight: 500;
  opacity: 0.6;
  margin-left: 2px;
}

.driver-list,
.quickwin-list {
  list-style: none;
}

.score-chip-row {
  gap: 6px;
  min-width: 0;
}

.score-chip {
  max-width: 100%;
  height: auto !important;
  min-height: 26px;
  white-space: normal;
}

.score-chip :deep(.v-chip__content) {
  min-width: 0;
  overflow: visible;
  text-overflow: unset;
  white-space: normal;
  line-height: 1.25;
  padding-top: 3px;
  padding-bottom: 3px;
}

.driver-item,
.quickwin-item {
  padding: 10px 0;
  border-bottom: 1px solid var(--v-border-color);
}

.driver-item:last-child,
.quickwin-item:last-child {
  border-bottom: none;
}

.driver-severity,
.quickwin-impact {
  flex-shrink: 0;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.7rem;
  min-width: 64px;
  justify-content: center;
}

.analysis-section--quickwins {
  background: rgba(var(--v-theme-success), 0.04);
  border-radius: 8px;
  margin-top: 8px;
}

.cost-footer {
  border-top: 1px solid var(--v-border-color);
  padding-top: 12px;
  margin-top: 8px;
  line-height: 1.4;
}

.skeleton-hero :deep(.v-skeleton-loader__heading) {
  width: 60%;
}

.skeleton-hero :deep(.v-skeleton-loader__chip) {
  margin-right: 4px;
}

.analysis-section {
  border-bottom: 1px solid var(--v-border-color);
  padding-top: 16px;
  padding-bottom: 16px;
}

.analysis-section:last-child {
  border-bottom: none;
}

.analysis-summary {
  background-color: var(--v-surface-variant);
  border-radius: 4px;
  position: relative;
}

.analysis-description {
  white-space: normal !important;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
}

.wait-message {
  color: var(--v-primary-base);
}

/* Standardize text styles */
.analysis-description :deep(p) {
  margin: 0.75rem 0;
  padding: 0.25rem 0;
  line-height: 1.6;
}

.analysis-description :deep(ul),
.analysis-description :deep(ol) {
  padding-left: 1.5rem;
  margin: 0.75rem 0;
  font-size: inherit;
}

.analysis-description :deep(li) {
  margin-bottom: 0.4rem;
  font-size: inherit;
}

.analysis-description :deep(code) {
  background-color: var(--v-surface-variant);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-size: var(--font-size-body-2);
}

.analysis-description :deep(blockquote) {
  border-left: 3px solid var(--v-primary-base);
  margin: 0.75rem 0;
  padding-left: 1rem;
  color: var(--text-secondary);
}

.analysis-description :deep(a) {
  color: var(--v-primary-base);
  text-decoration: none;
}

.analysis-description :deep(a:hover) {
  text-decoration: underline;
}

.analysis-description :deep(h1),
.analysis-description :deep(.text-h1) {
  font-size: var(--font-size-h4) !important;
  color: var(--text-primary) !important;
  font-weight: var(--font-weight-semibold) !important;
  line-height: var(--line-height-snug) !important;
  margin: 1rem 0 0.75rem !important;
}

.analysis-description :deep(h2),
.analysis-description :deep(.text-h2) {
  font-size: var(--font-size-h5) !important;
  color: var(--text-primary) !important;
  font-weight: var(--font-weight-semibold) !important;
  line-height: var(--line-height-snug) !important;
  margin: 0.85rem 0 0.6rem !important;
}

.analysis-description :deep(h3),
.analysis-description :deep(.text-h3),
.analysis-description :deep(.text-h4),
.analysis-description :deep(h4),
.analysis-description :deep(.text-h5),
.analysis-description :deep(.text-subtitle-1),
.analysis-description :deep(.text-subtitle-2) {
  font-size: var(--font-size-h6) !important;
  color: var(--text-primary) !important;
  font-weight: var(--font-weight-semibold) !important;
  line-height: var(--line-height-snug) !important;
  margin: 0.75rem 0 0.5rem !important;
}

/* Remove custom typography styles and let Vuetify handle it */
:deep(.text-medium-emphasis) {
  opacity: 0.7;
}

.premium-item {
  position: relative;
}

.premium-item:hover .premium-message {
  display: flex;
}

/* Add styles for formatted text */
:deep(strong) {
  font-weight: var(--font-weight-semibold);
}

:deep(em) {
  font-style: italic;
  color: var(--v-primary-base);
}

/* Typography and text styles */
.analysis-description {
  white-space: normal !important;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
}

/* Remove custom typography styles and let Vuetify handle it */
:deep(.v-card-title) {
  font-size: inherit;
  font-weight: inherit;
}

/* Rest of existing styles... */
</style>

